"use client";

/**
 * Yandex SmartCaptcha — чекбокс «Я не робот» для форм лендинга.
 * Грузит скрипт капчи один раз на страницу, рендерит виджет и отдаёт токен
 * через onToken (пустая строка — токен сброшен/истёк). Если клиентский ключ
 * (NEXT_PUBLIC_YANDEX_SMARTCAPTCHA_KEY) не задан — компонент инертен (ничего не
 * рисует), чтобы формы работали до подключения капчи. Серверная проверка токена
 * — в /api/lead.
 */

import { useEffect, useRef } from "react";

const SITE_KEY = process.env.NEXT_PUBLIC_YANDEX_SMARTCAPTCHA_KEY || "";

export const captchaEnabled = !!SITE_KEY;

declare global {
  interface Window {
    smartCaptcha?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => number;
      subscribe: (id: number, ev: string, cb: () => void) => void;
      destroy?: (id: number) => void;
    };
  }
}

// Грузим captcha.js один раз; render=onload — отдаём контроль над рендером нам.
let scriptPromise: Promise<void> | null = null;
function loadCaptchaScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.smartCaptcha) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve) => {
    const ready = () => {
      const t = setInterval(() => {
        if (window.smartCaptcha) {
          clearInterval(t);
          resolve();
        }
      }, 50);
    };
    const existing = document.querySelector("script[data-smartcaptcha]");
    if (existing) {
      ready();
      return;
    }
    const s = document.createElement("script");
    s.src = "https://smartcaptcha.yandexcloud.net/captcha.js?render=onload";
    s.async = true;
    s.defer = true;
    s.setAttribute("data-smartcaptcha", "1");
    s.onload = ready;
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export function SmartCaptcha({ onToken }: { onToken: (token: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const widgetId = useRef<number | null>(null);

  useEffect(() => {
    if (!SITE_KEY) return;
    let cancelled = false;
    loadCaptchaScript().then(() => {
      if (cancelled || !ref.current || !window.smartCaptcha || widgetId.current != null) return;
      widgetId.current = window.smartCaptcha.render(ref.current, {
        sitekey: SITE_KEY,
        hl: "ru",
        callback: (token: string) => onToken(token),
      });
      window.smartCaptcha.subscribe(widgetId.current, "token-expired", () => onToken(""));
      window.smartCaptcha.subscribe(widgetId.current, "network-error", () => onToken(""));
    });
    return () => {
      cancelled = true;
      if (widgetId.current != null && window.smartCaptcha?.destroy) {
        window.smartCaptcha.destroy(widgetId.current);
        widgetId.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!SITE_KEY) return null;
  return <div ref={ref} style={{ margin: "4px 0" }} />;
}
